import { useState } from "react";
import { Plus, Trash, Check, DotsSixVertical, PencilSimple } from "@phosphor-icons/react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { nid, FIELD_TYPES, EMPTY_FIELD } from "./wizardShared";

export default function StepSchema({ pack, setPack }) {
  const [selSec, setSelSec] = useState(0);
  const [newSec, setNewSec] = useState("");
  const [secDrag, setSecDrag] = useState(null);
  const [fieldDrag, setFieldDrag] = useState(null);
  const [fieldDraft, setFieldDraft] = useState(EMPTY_FIELD);
  const [editFieldIdx, setEditFieldIdx] = useState(null);

  const setSections = (fn) => setPack((p) => ({ ...p, sections: fn(p.sections) }));
  const section = pack.sections[selSec];

  const saveField = () => {
    if (!fieldDraft.id || !fieldDraft.label) { toast.error("Field ID and Label are required"); return; }
    if (!/^[a-z0-9_]+$/.test(fieldDraft.id)) { toast.error("Field ID must be a slug: lowercase letters, numbers, underscores"); return; }
    const f = { id: fieldDraft.id, label: fieldDraft.label, type: fieldDraft.type, unit: fieldDraft.unit, required: fieldDraft.required, help: fieldDraft.help, ai_hint: fieldDraft.ai_hint };
    if (fieldDraft.type === "select" || fieldDraft.type === "multi-select")
      f.options = fieldDraft.optionsText.split(",").map((s) => s.trim()).filter(Boolean);
    if (fieldDraft.default !== "") f.default = fieldDraft.type === "number" ? Number(fieldDraft.default) : fieldDraft.default;
    if (fieldDraft.min !== "") f.min = Number(fieldDraft.min);
    if (fieldDraft.max !== "") f.max = Number(fieldDraft.max);
    if (editFieldIdx === null && section.fields.some((x) => x.id === f.id)) { toast.error("Field ID already exists in this section"); return; }
    setSections((ss) => ss.map((s, i) => {
      if (i !== selSec) return s;
      if (editFieldIdx !== null) return { ...s, fields: s.fields.map((x, xi) => (xi === editFieldIdx ? f : x)) };
      return { ...s, fields: [...s.fields, f] };
    }));
    setFieldDraft(EMPTY_FIELD);
    setEditFieldIdx(null);
  };

  const dropSec = (idx) => {
    if (secDrag === null || secDrag === idx) return;
    setSections((ss) => { const n = [...ss]; const [m] = n.splice(secDrag, 1); n.splice(idx, 0, m); return n; });
    setSelSec(idx);
    setSecDrag(null);
  };

  const dropField = (idx) => {
    if (fieldDrag === null || fieldDrag === idx) return;
    setSections((ss) => ss.map((s, i) => {
      if (i !== selSec) return s;
      const n = [...s.fields]; const [m] = n.splice(fieldDrag, 1); n.splice(idx, 0, m);
      return { ...s, fields: n };
    }));
    setFieldDrag(null);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" data-testid="wizard-schema">
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-3">
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] px-1 mb-2">Sections</div>
        <div className="space-y-1">
          {pack.sections.map((s, i) => (
            <div key={s.id} draggable onDragStart={() => setSecDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => dropSec(i)}
              onClick={() => setSelSec(i)} data-testid={`schema-section-${s.id}`}
              className={`flex items-center gap-1.5 px-2 py-2 rounded-sm cursor-pointer text-xs transition-colors duration-150 ${
                i === selSec ? "bg-[var(--c-accent)]/15 text-[var(--c-text)] border border-[var(--c-accent)]/40" : "text-[var(--c-muted)] hover:bg-[var(--c-surface-2)] border border-transparent"}`}>
              <DotsSixVertical size={12} className="cursor-grab shrink-0" />
              <span className="flex-1 truncate">{s.name}</span>
              <span className="font-mono text-[9px]">{s.fields.length}</span>
              <button type="button" data-testid={`schema-section-del-${s.id}`}
                onClick={(e) => { e.stopPropagation(); setSections((ss) => ss.filter((_, xi) => xi !== i)); setSelSec(0); }}
                className="p-0.5 hover:text-[var(--c-red)]"><Trash size={11} /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-1.5 mt-3">
          <Input data-testid="schema-new-section" value={newSec} onChange={(e) => setNewSec(e.target.value)}
            placeholder="New section" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-7 text-xs" />
          <Button type="button" size="sm" data-testid="schema-add-section"
            onClick={() => { const n = newSec.trim(); if (!n) return; setSections((ss) => [...ss, { id: nid(), name: n, fields: [] }]); setNewSec(""); setSelSec(pack.sections.length); }}
            className="bg-[var(--c-border)] hover:bg-[var(--c-border)]/70 text-[var(--c-text)] h-7 rounded-sm"><Plus size={12} /></Button>
        </div>
      </div>

      <div className="lg:col-span-3 space-y-4">
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm overflow-x-auto">
          <div className="px-4 py-2.5 border-b border-[var(--c-border)] text-xs font-medium text-[var(--c-text)]">
            Fields · {section?.name || "no section selected"}
          </div>
          {section && section.fields.length > 0 ? (
            <table className="w-full text-xs" data-testid="schema-fields-table">
              <thead>
                <tr className="border-b border-[var(--c-border)]">
                  {["", "Field ID", "Label", "Type", "Unit", "Req", "Options", ""].map((h, i) => (
                    <th key={i} className="text-left px-3 py-2 text-[9px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {section.fields.map((f, i) => (
                  <tr key={f.id} draggable onDragStart={() => setFieldDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => dropField(i)}
                    data-testid={`schema-field-row-${f.id}`} className="border-b border-[var(--c-border)]/50 last:border-0 hover:bg-[var(--c-border)]/25">
                    <td className="px-3 py-2"><DotsSixVertical size={12} className="text-[var(--c-muted)] cursor-grab" /></td>
                    <td className="px-3 py-2 font-mono text-[var(--c-accent)]">{f.id}</td>
                    <td className="px-3 py-2 text-[var(--c-text)]">{f.label}</td>
                    <td className="px-3 py-2 text-[var(--c-muted)]">{f.type}</td>
                    <td className="px-3 py-2 font-mono text-[var(--c-muted)]">{f.unit || "—"}</td>
                    <td className="px-3 py-2">{f.required ? <span className="text-[var(--c-red)]">*</span> : "—"}</td>
                    <td className="px-3 py-2 text-[var(--c-muted)] max-w-32 truncate">{(f.options || []).join(", ") || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button type="button" data-testid={`schema-field-edit-${f.id}`}
                        onClick={() => { setEditFieldIdx(i); setFieldDraft({ ...EMPTY_FIELD, ...f, optionsText: (f.options || []).join(", "), default: f.default ?? "", min: f.min ?? "", max: f.max ?? "" }); }}
                        className="p-1 text-[var(--c-muted)] hover:text-[var(--c-accent)]"><PencilSimple size={12} /></button>
                      <button type="button" data-testid={`schema-field-del-${f.id}`}
                        onClick={() => setSections((ss) => ss.map((s, si) => (si === selSec ? { ...s, fields: s.fields.filter((_, xi) => xi !== i) } : s)))}
                        className="p-1 text-[var(--c-muted)] hover:text-[var(--c-red)]"><Trash size={12} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-4 py-8 text-center text-xs text-[var(--c-muted)]" data-testid="schema-no-fields">
              {section ? "No fields yet — add one below." : "Add a section first."}
            </div>
          )}
        </div>

        {section && (
          <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4" data-testid="field-form">
            <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">
              {editFieldIdx !== null ? `Edit field · ${fieldDraft.id}` : "Add field"}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] text-[var(--c-muted)]">Field ID (slug)</Label>
                <Input data-testid="ff-id" value={fieldDraft.id} onChange={(e) => setFieldDraft({ ...fieldDraft, id: e.target.value })}
                  placeholder="rating_kva" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs font-mono" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-[var(--c-muted)]">Label</Label>
                <Input data-testid="ff-label" value={fieldDraft.label} onChange={(e) => setFieldDraft({ ...fieldDraft, label: e.target.value })}
                  placeholder="Rating" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-[var(--c-muted)]">Type</Label>
                <Select value={fieldDraft.type} onValueChange={(v) => setFieldDraft({ ...fieldDraft, type: v })}>
                  <SelectTrigger data-testid="ff-type" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                    {FIELD_TYPES.map((t) => <SelectItem key={t} value={t} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-[var(--c-muted)]">Unit</Label>
                <Input data-testid="ff-unit" value={fieldDraft.unit} onChange={(e) => setFieldDraft({ ...fieldDraft, unit: e.target.value })}
                  placeholder="kVA" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs font-mono" />
              </div>
              {(fieldDraft.type === "select" || fieldDraft.type === "multi-select") && (
                <div className="space-y-1 col-span-2">
                  <Label className="text-[10px] text-[var(--c-muted)]">Options (comma separated)</Label>
                  <Input data-testid="ff-options" value={fieldDraft.optionsText} onChange={(e) => setFieldDraft({ ...fieldDraft, optionsText: e.target.value })}
                    placeholder="ONAN, ONAF, OFAF" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-[10px] text-[var(--c-muted)]">Default value</Label>
                <Input data-testid="ff-default" value={fieldDraft.default} onChange={(e) => setFieldDraft({ ...fieldDraft, default: e.target.value })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
              </div>
              {fieldDraft.type === "number" && (
                <>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-[var(--c-muted)]">Min</Label>
                    <Input data-testid="ff-min" type="number" value={fieldDraft.min} onChange={(e) => setFieldDraft({ ...fieldDraft, min: e.target.value })}
                      className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs font-mono" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-[var(--c-muted)]">Max</Label>
                    <Input data-testid="ff-max" type="number" value={fieldDraft.max} onChange={(e) => setFieldDraft({ ...fieldDraft, max: e.target.value })}
                      className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs font-mono" />
                  </div>
                </>
              )}
              <div className="space-y-1 col-span-2">
                <Label className="text-[10px] text-[var(--c-muted)]">Help text (tooltip)</Label>
                <Input data-testid="ff-help" value={fieldDraft.help} onChange={(e) => setFieldDraft({ ...fieldDraft, help: e.target.value })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-[10px] text-[var(--c-muted)]">AI extraction hint</Label>
                <Input data-testid="ff-ai-hint" value={fieldDraft.ai_hint} onChange={(e) => setFieldDraft({ ...fieldDraft, ai_hint: e.target.value })}
                  placeholder="Look for rated power, transformer rating, kVA" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
              </div>
              <div className="flex items-center gap-2 pt-4">
                <Switch data-testid="ff-required" checked={fieldDraft.required} onCheckedChange={(v) => setFieldDraft({ ...fieldDraft, required: v })} />
                <Label className="text-[10px] text-[var(--c-muted)]">Required</Label>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button type="button" data-testid="ff-save" onClick={saveField}
                className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
                <Check size={13} className="mr-1" /> {editFieldIdx !== null ? "Update Field" : "Add Field"}
              </Button>
              {editFieldIdx !== null && (
                <Button type="button" variant="outline" onClick={() => { setEditFieldIdx(null); setFieldDraft(EMPTY_FIELD); }}
                  className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] rounded-sm h-8 text-xs">Cancel</Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
